/**
 * EmoneyPoSChart component module.
 * @module shared/widgets/EmoneyPoSChart
 */
import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more.js';
import solidGauge from 'highcharts/modules/solid-gauge.js';
import styled from 'styled-components/macro';

highchartsMore(Highcharts);
solidGauge(Highcharts);

const _Wrapper = styled.div`
  height: ${props => props.height}px;
  width: ${props => props.width}px;

  .highcharts-container {
    height: ${props => props.height}px !important;
    width: ${props => props.width}px !important;
  }
`;

const breakpoints = {
  md: {
    height: 200,
    width: 200,
    size: '100%',
    center: ['50%', '70%'],
    centerLabel: {
      position: -17,
      numberFontSize: '20px',
    },
    axisLabel: {
      position: 15,
      fontSize: '12px',
    },
    arrowWidth: 20,
    noData: {
      numberPosition: 8,
      labelPosition: -30,
    },
  },
  sm: {
    height: 130,
    width: 130,
    size: '100%',
    center: ['50%', '70%'],
    centerLabel: {
      position: -17,
      numberFontSize: '20px',
    },
    axisLabel: {
      position: 12,
      fontSize: '10px',
    },
    arrowWidth: 20,
    noData: {
      numberPosition: 8,
      labelPosition: -30,
    },
  },
  xs: {
    height: 40,
    width: 75,
    size: '170%',
    center: ['50%', '90%'],
    centerLabel: {
      position: -14,
      numberFontSize: '12px',
    },
    axisLabel: {
      position: null,
      fontSize: '0px',
    },
    arrowWidth: 10,
    noData: {
      numberPosition: 11,
      labelPosition: null,
    },
  },
};

/**
 * EmoneyPoSChart component
 * @name EmoneyPoSChart
 */
const EmoneyPoSChart = (props) => {
  const {probabilityOfSuccess, layoutSize = 'sm'} = props;

  return <_Wrapper width={breakpoints[layoutSize].width} height={breakpoints[layoutSize].height}>
    <HighchartsReact
      containerProps={{
        style: {width: '100%', height: '100%'},
      }}
      highcharts={Highcharts}
      options={{
        chart: {
          type: 'solidgauge',
          backgroundColor: 'rgba(255,255,255,0)',
          animation: false,
          height: breakpoints[layoutSize].height,
          plotBackgroundColor: null,
          plotBackgroundImage: null,
          plotBorderWidth: 0,
          plotShadow: false,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0],
        },
        tooltip: {
          enabled: false,
        },
        title: {
          text: null,
        },
        pane: {
          size: breakpoints[layoutSize].size,
          center: breakpoints[layoutSize].center,
          startAngle: -90,
          endAngle: 90,
          background: {
            backgroundColor: '#494f52',
            innerRadius: '60%',
            outerRadius: '100%',
            shape: 'arc',
            borderColor: 'rgba(0,255,0,0)',
          },
        },
        yAxis: {
          stops: [
            [0, '#00AEEF'],
          ],
          lineWidth: 0,
          tickWidth: 0,
          minorTickInterval: null,
          tickAmount: 2,
          labels: {
            y: breakpoints[layoutSize].axisLabel.position,
            x: 0,
            distance: '80%',
            style: {
              fontSize: breakpoints[layoutSize].axisLabel.fontSize,
              color: '#FFFFFF',
            },
            formatter: function percentVal() {
              return this.value + '%';
            },
          },
          min: 0,
          max: 100,
        },
        plotOptions: {
          solidgauge: {
            clip: false,
            dataLabels: {
              y: breakpoints[layoutSize].centerLabel.position,
              borderWidth: 0,
              useHTML: true,
            },
          },
          area: {
            animation: true,
          },
        },
        credits: {
          enabled: false,
        },
        series: [{
          name: 'POS',
          data: [probabilityOfSuccess],
          dataLabels: {
            formatter: () => probabilityOfSuccess + '%',
            style: {
              color: 'white',
              fontSize: breakpoints[layoutSize].centerLabel.numberFontSize,
              fontFamily: 'ProximaRegular',
            },
            crop: false,
            overflow: 'allow',
          },
        }],
      }}
    />
  </_Wrapper>;
};

export default EmoneyPoSChart;

EmoneyPoSChart.propTypes = {
  probabilityOfSuccess: PropTypes.number,
  /**
   * The size of the component ('xs', 'sm', or 'md')
   */
  layoutSize: PropTypes.string,
};
